// Shared types between client and server
import type { SphinxModel } from "./sphinx";

import type {
  AuthMethod,
  IdentityAssertionFormat,
  RegistrationMode,
  XaaClientAuthMethod,
} from "./xaa";

// Legacy server config (keeping for compatibility)
export interface ServerConfig {
  id: string;
  name: string;
  command: string;
  args?: string[];
  env?: Record<string, string>;
  cwd?: string;
}

// Chat and messaging types
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  attachments?: Attachment[];
  toolCalls?: ToolCall[];
  toolResults?: ToolResult[];
  metadata?: MessageMetadata;
}

export interface ToolCall {
  id: string | number;
  name: string;
  parameters: Record<string, any>;
  timestamp: Date;
  status: "pending" | "executing" | "completed" | "error";
  result?: any;
  error?: string;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  contentType: string;
  size?: number;
}

export interface ToolResult {
  id: string;
  toolCallId: string;
  result: any;
  error?: string;
  timestamp: Date;
}

export interface MessageMetadata {
  createdAt: string;
  editedAt?: string;
  regenerated?: boolean;
  tokens?: {
    input: number;
    output: number;
  };
}

export interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  error?: string;
  connectionStatus: "connected" | "disconnected" | "connecting";
}

export interface ChatActions {
  sendMessage: (content: string, attachments?: Attachment[]) => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  regenerateMessage: (messageId: string) => Promise<void>;
  deleteMessage: (messageId: string) => void;
  clearChat: () => void;
  stopGeneration: () => void;
}

export interface MCPToolCall extends ToolCall {
  serverId: string;
  serverName: string;
}

export interface MCPToolResult extends ToolResult {
  serverId: string;
}

export type ChatStatus = "idle" | "typing" | "streaming" | "error";

export interface StreamingMessage {
  id: string;
  content: string;
  isComplete: boolean;
}

// Model definitions
//
// sphinx talks to exactly two providers and exactly four models. The model ids
// are the SPHINX_MODELS vocabulary in ./sphinx.ts; this section is the typed
// catalog (display name, provider, context length) built on top of it.
export type ModelProvider = "anthropic" | "openai";

export const MODEL_PROVIDERS: readonly ModelProvider[] = ["anthropic", "openai"];

export function isModelProvider(value: unknown): value is ModelProvider {
  return (MODEL_PROVIDERS as readonly unknown[]).includes(value);
}

// Every model is BYOK (the user's own Anthropic / OpenAI key). There are no
// Sphinx-hosted ("free") models any more, so the hosted classification is a
// constant `false`. Kept as functions so the (many) call sites that ask "is
// this a hosted model?" read the same way they always did.
export const SPHINX_PROVIDED_MODEL_IDS: readonly string[] = [];

export const isSphinxProvidedModel = (
  _modelId: string,
  _provider?: string
): boolean => false;

export const isSphinxGuestAllowedModel = (
  _modelId: string,
  _provider?: string
): boolean => false;

/** Minimal shape `getCanonicalModelId` matches against. */
export type CanonicalModelCandidate = { id: string | Model; provider: string };

/**
 * Canonicalize a model id against the catalog: trims, and resolves a
 * provider-prefixed spelling ("anthropic/claude-opus-5") or a bare id to the
 * catalog id when one matches. Unknown ids pass through trimmed.
 */
export const getCanonicalModelId = (
  modelId: string,
  provider?: string,
  extraModels: readonly CanonicalModelCandidate[] = []
): string => {
  const normalizedModelId = modelId.trim();
  if (!normalizedModelId) {
    return normalizedModelId;
  }

  const knownModels: readonly CanonicalModelCandidate[] =
    extraModels.length > 0
      ? [...SUPPORTED_MODELS, ...extraModels]
      : SUPPORTED_MODELS;

  const normalizedProvider = provider?.trim().toLowerCase();
  const bareId = normalizedModelId.includes("/")
    ? normalizedModelId.slice(normalizedModelId.lastIndexOf("/") + 1)
    : normalizedModelId;

  if (normalizedProvider) {
    const providerScopedMatch = knownModels.find(
      (model) =>
        model.provider.toLowerCase() === normalizedProvider &&
        (String(model.id) === normalizedModelId || String(model.id) === bareId)
    );
    if (providerScopedMatch) {
      return String(providerScopedMatch.id);
    }
  }

  const exactMatch = knownModels.find(
    (model) => String(model.id) === normalizedModelId
  );
  if (exactMatch) {
    return String(exactMatch.id);
  }

  return normalizedModelId;
};

/**
 * Ids that reject sampling parameters: the Claude 5 family and GPT-5.x answer
 * a `temperature` with a 400 rather than ignoring it, so sending one fails the
 * whole request. Matched by prefix as well as exactly, so a dated snapshot
 * ("claude-opus-5-20260401") resolves like the alias it pins.
 */
const MODEL_IDS_REJECTING_TEMPERATURE = ["claude-fable-5", "claude-opus-5"];

export const modelSupportsTemperature = (modelId: string | Model): boolean => {
  const id = String(modelId);
  if (id.includes("gpt-5")) {
    return false;
  }
  const bareId = id.slice(id.lastIndexOf("/") + 1);
  return !MODEL_IDS_REJECTING_TEMPERATURE.some(
    (rejected) => bareId === rejected || bareId.startsWith(`${rejected}-`)
  );
};

export interface ModelDefinition {
  id: Model | string;
  name: string;
  provider: ModelProvider;
  contextLength?: number;
  disabled?: boolean;
  disabledReason?: string;
}

export enum Model {
  CLAUDE_OPUS_5 = "claude-opus-5",
  CLAUDE_FABLE_5 = "claude-fable-5",
  GPT_5_6_LUNA = "gpt-5.6-luna",
  GPT_5_6_SOL = "gpt-5.6-sol",
}

// Compile-time guard: the enum and the SPHINX_MODELS vocabulary are the same
// set of ids. Adding to one without the other fails here.
const _modelEnumIsSphinxVocabulary: Record<SphinxModel, Model> = {
  "claude-opus-5": Model.CLAUDE_OPUS_5,
  "claude-fable-5": Model.CLAUDE_FABLE_5,
  "gpt-5.6-luna": Model.GPT_5_6_LUNA,
  "gpt-5.6-sol": Model.GPT_5_6_SOL,
};
void _modelEnumIsSphinxVocabulary;

/** The complete selectable catalog. Every entry is BYOK. */
export const SUPPORTED_MODELS: ModelDefinition[] = [
  {
    id: Model.CLAUDE_OPUS_5,
    name: "Claude Opus 5",
    provider: "anthropic",
    contextLength: 1000000,
  },
  {
    id: Model.CLAUDE_FABLE_5,
    name: "Claude Fable 5",
    provider: "anthropic",
    contextLength: 1000000,
  },
  {
    id: Model.GPT_5_6_LUNA,
    name: "GPT-5.6 Luna",
    provider: "openai",
    contextLength: 400000,
  },
  {
    id: Model.GPT_5_6_SOL,
    name: "GPT-5.6 Sol",
    provider: "openai",
    contextLength: 400000,
  },
];

// Helper functions for models
export const getModelById = (id: string): ModelDefinition | undefined => {
  return SUPPORTED_MODELS.find((model) => model.id === id);
};

export const isModelSupported = (id: string): boolean => {
  return SUPPORTED_MODELS.some((model) => model.id === getCanonicalModelId(id));
};

/**
 * The concrete connect-form OAuth protocol eras, in chronological order
 * (oldest first). The dropdown (AuthenticationSection's PROTOCOL_OPTIONS)
 * renders newest-first separately; this tuple's order is not the UI order.
 * SINGLE SOURCE OF TRUTH: both the {@link ServerFormOAuthProtocolMode} union
 * and {@link normalizeOauthProtocolMode}'s membership check derive from this
 * tuple, so a new era is added in exactly one place and the two can no longer
 * drift on which values survive a stored/prefilled pin.
 */
export const SERVER_FORM_OAUTH_PROTOCOL_MODES = [
  "2025-03-26",
  "2025-06-18",
  "2025-11-25",
  "2026-07-28",
] as const;

/** A resolved connect-form OAuth protocol era (never the "auto" sentinel). */
export type ServerFormOAuthProtocolConcreteMode =
  (typeof SERVER_FORM_OAUTH_PROTOCOL_MODES)[number];

export type ServerFormOAuthProtocolMode =
  | "auto"
  | ServerFormOAuthProtocolConcreteMode;

/**
 * The default concrete era used when nothing is stored and "auto" cannot be
 * biased by a wire pin. Deliberately not the newest era: an unpinned server
 * gets the widely-deployed November flow, not the one servers are still
 * catching up to.
 */
export const DEFAULT_OAUTH_PROTOCOL_CONCRETE_MODE: ServerFormOAuthProtocolConcreteMode =
  "2025-11-25";

export function isConcreteOauthProtocolMode(
  value: string
): value is ServerFormOAuthProtocolConcreteMode {
  return (SERVER_FORM_OAUTH_PROTOCOL_MODES as readonly string[]).includes(value);
}

export function isServerFormOAuthProtocolMode(
  value: unknown
): value is ServerFormOAuthProtocolMode {
  return (
    value === "auto" ||
    (typeof value === "string" && isConcreteOauthProtocolMode(value))
  );
}

/**
 * Coerce an arbitrary stored/prefilled protocol string to a connect-form OAuth
 * protocol mode, preserving both the 2026-07-28 draft era AND the deferred
 * "auto" sentinel. "auto" is a valid {@link ServerFormOAuthProtocolMode} that
 * the wire-pin bridge resolves later, so it MUST survive hydration — coercing
 * it to a concrete era here drops the sentinel and makes the bridge unreachable
 * for prefilled/edited servers. Only unknown values and `undefined` fall back
 * to the current concrete default (2025-11-25). Single-sourced here so the Add
 * and Edit connect-form paths cannot drift: they previously kept private,
 * hand-maintained copies that both had to be patched in lockstep to avoid
 * silently degrading a stored 2026-07-28 pin down to 2025-11-25.
 */
export function normalizeOauthProtocolMode(
  value?: string
): ServerFormOAuthProtocolMode {
  if (value === "auto") {
    return "auto";
  }
  return value != null && isConcreteOauthProtocolMode(value)
    ? value
    : DEFAULT_OAUTH_PROTOCOL_CONCRETE_MODE;
}

/**
 * Resolve a connect-form OAuth protocol mode to the concrete era the flow will
 * actually run. An explicit era passes straight through — it always wins. The
 * deferred "auto" sentinel resolves from concrete MCP evidence. An explicit
 * wire pin wins, followed by the version negotiated during initialization.
 * When authentication prevents negotiation there is no version evidence, so
 * Auto uses the 2025-11-25 compatibility flow.
 *
 * Keep the Auto sentinel in persisted form data; callers use this resolver
 * only when they need the concrete version for an OAuth session or preview.
 */
export function resolveEffectiveOauthProtocolMode(
  mode: ServerFormOAuthProtocolMode,
  wireProtocolVersion?: string,
  negotiatedProtocolVersion?: string
): ServerFormOAuthProtocolConcreteMode {
  if (mode !== "auto") {
    return mode;
  }
  if (
    wireProtocolVersion !== undefined &&
    isConcreteOauthProtocolMode(wireProtocolVersion)
  ) {
    return wireProtocolVersion;
  }
  if (
    negotiatedProtocolVersion !== undefined &&
    isConcreteOauthProtocolMode(negotiatedProtocolVersion)
  ) {
    return negotiatedProtocolVersion;
  }
  return DEFAULT_OAUTH_PROTOCOL_CONCRETE_MODE;
}

/**
 * Resolve canonical intent plus compatibility data into the concrete version
 * that must be frozen into one OAuth session. Records created before
 * `oauthProtocolMode` existed retain their concrete profile as an explicit
 * choice; new Auto records never reuse a previous flow's concrete result.
 */
export function resolveOAuthProtocolSelection(input: {
  mode?: ServerFormOAuthProtocolMode;
  legacyProtocolVersion?: string;
  wireProtocolVersion?: string;
  negotiatedProtocolVersion?: string;
}): {
  mode: ServerFormOAuthProtocolMode;
  protocolVersion: ServerFormOAuthProtocolConcreteMode;
  source:
    | "explicit_oauth"
    | "wire_pin"
    | "negotiated"
    | "auth_gated_fallback";
} {
  const mode =
    input.mode ??
    (input.legacyProtocolVersion !== undefined &&
    isConcreteOauthProtocolMode(input.legacyProtocolVersion)
      ? input.legacyProtocolVersion
      : "auto");
  return {
    mode,
    protocolVersion: resolveEffectiveOauthProtocolMode(
      mode,
      input.wireProtocolVersion,
      input.negotiatedProtocolVersion
    ),
    source:
      mode !== "auto"
        ? "explicit_oauth"
        : input.wireProtocolVersion !== undefined &&
          isConcreteOauthProtocolMode(input.wireProtocolVersion)
        ? "wire_pin"
        : input.negotiatedProtocolVersion !== undefined &&
          isConcreteOauthProtocolMode(input.negotiatedProtocolVersion)
        ? "negotiated"
        : "auth_gated_fallback",
  };
}

/**
 * @deprecated Use {@link RegistrationMode} (re-exported from shared/xaa) — the
 * unified client-registration vocabulary shared by the OAuth flows and the
 * XAA debugger. Structurally identical; kept as an alias for older imports.
 */
export type ServerFormOAuthRegistrationMode = RegistrationMode;

/**
 * The auth type a server-form row is configured with. "xaa" (Cross-App Access)
 * is a distinct flow from "oauth": the inspector server mints the access token
 * server-side via token-exchange rather than running a browser OAuth flow.
 * "auto" resolves at connect time: XAA when the server is XAA-configured
 * (IdP mode + stored client id) — a hard selection, never a fallback after a
 * failed mint — and otherwise "discover": use a stored OAuth token when one
 * exists, else connect unauthenticated and escalate to interactive OAuth only
 * when the server answers 401 (the MCP spec's canonical discovery sequence).
 */
export type ServerFormAuthType = "auto" | "oauth" | "bearer" | "none" | "xaa";

export interface ServerFormData {
  name: string;
  type: "stdio" | "http";
  command?: string;
  args?: string[];
  url?: string;
  headers?: Record<string, string>;
  env?: Record<string, string>;
  /**
   * EXPLICIT secret write. The plain `env` / `headers` above feed the
   * in-memory connect only — persistence (Convex) writes secrets exclusively
   * from this patch, via `buildSecretSyncOptions`, and reads key PRESENCE as
   * intent:
   *
   * - key ABSENT       → leave the stored value untouched (an edit that never
   *                      opened the secrets section must not clobber it);
   * - key present `{}`  → explicitly CLEAR the stored value;
   * - key present, set  → replace the stored value.
   *
   * That three-state contract is why the plain fields cannot simply be
   * persisted as a fallback: there, an untouched edit form and a deliberate
   * clear are indistinguishable.
   *
   * The consequence for producers: anything holding REAL secret values — a JSON
   * import, a bundle install, a deep link — has to set this too, and only when
   * the values are non-empty (an empty patch would wipe the credentials of an
   * existing server with the same name). Setting just `env` / `headers` yields
   * a server that connects once from memory and comes back credential-less
   * after a reload, with nothing to indicate anything was dropped.
   */
  secretPatch?: {
    env?: Record<string, string>;
    headers?: Record<string, string>;
  };
  clientCapabilities?: Record<string, unknown>;
  useOAuth?: boolean;
  /**
   * Canonical auth method (Convex `authMethod` column). The useOAuth/useXaa
   * booleans are its derived compat mirrors — the backend re-derives them on
   * every write. "auto" selects XAA when the server is XAA-configured, and
   * otherwise "discover" (stored token if present, else unauthenticated with
   * OAuth escalation on 401) — see server/utils/effective-auth.ts.
   */
  authMethod?: AuthMethod;
  /**
   * Reset boundary: when true the backend nulls the sticky XAA identity
   * config (authServerMode, xaaSubject, xaaEmail). Sent when a modal save
   * explicitly moves the server's auth type off XAA; plain saves preserve.
   */
  clearXaaConfig?: boolean;
  oauthProtocolMode?: ServerFormOAuthProtocolMode;
  /**
   * Per-server MCP wire-version pin chosen at ADD time (the edit flow writes
   * it directly through `projectServerConfig:setConfig` instead of the form
   * payload). Persisted on the project layer
   * (`projectServerRefs.mcpProtocolVersionOverride`), NOT on the server's own
   * config blob — the add flow applies it once the hosted server row exists,
   * then reconnects. Undefined = inherit host default / SDK default.
   */
  mcpProtocolVersionOverride?: import("@sphinx/sdk/browser").McpProtocolVersion;
  /**
   * Unified client-registration mode (Client↔AS leg) shared by the OAuth
   * flows and the XAA debugger: how this server's client establishes its
   * identity at the authorization server. "auto" resolves to a concrete
   * strategy at flow time (OAuth: resolveAuthorizationPlan; XAA debugger:
   * falls back to preregistered). Persisted per-server (Convex
   * `registrationMode` column). Replaces `oauthRegistrationMode` and the
   * XAA-only `xaaRegistrationStrategy`.
   */
  registrationMode?: RegistrationMode;
  oauthScopes?: string[];
  clientId?: string;
  clientSecret?: string;
  hasClientSecret?: boolean;
  clearClientSecret?: boolean;
  /** Optional issuer override for the cross-app authorization test target. */
  xaaAuthzIssuer?: string;
  /**
   * Opt-in: accept a path-scoped authorization server whose metadata
   * advertises the same-origin root as issuer (multi-tenant AS deployments
   * with path-scoped issuers). Off = strict RFC 8414 issuer match.
   */
  xaaAllowPathScopedIssuer?: boolean;
  /**
   * The OAuth Debugger's equivalent of `xaaAllowPathScopedIssuer`, kept as a
   * separate per-server field so enabling the relaxation for one debugger does
   * not silently widen trust in the other. Off = strict RFC 8414 issuer match.
   */
  oauthAllowPathScopedIssuer?: boolean;
  /**
   * Cross-App Access (XAA) connect flag. When true the server authenticates via
   * the XAA token-exchange flow rather than standard OAuth. Mutually exclusive
   * with `useOAuth` — the form only ever sets one.
   */
  useXaa?: boolean;
  /**
   * Which identity provider mints the XAA assertion. v1 only writes "mcpjam"
   * (the built-in test IdP); "own" is reserved for the bring-your-own-IdP
   * follow-up.
   */
  authServerMode?: "mcpjam" | "own";
  /** Optional simulated-identity override (subject) for the Sphinx test IdP. Blank = signed-in user. */
  xaaSubject?: string;
  /** Optional simulated-identity override (email) for the Sphinx test IdP. Blank = signed-in user. */
  xaaEmail?: string;
  /**
   * XAA-debugger-only preset for the identity assertion the Sphinx test IdP
   * mints (draft-ietf-oauth-identity-assertion-authz-grant): "oidc" (default)
   * mints an ID token; "saml" mints a signed SAML assertion AND asks for a
   * saml-nameid `sub_id` on the ID-JAG. Persisted per-server; saves that omit
   * it must preserve the stored value (the save-path `?? existing` merge).
   */
  xaaIdentityAssertionFormat?: IdentityAssertionFormat;
  /**
   * XAA-debugger-only CIMD client authentication ("none" public default |
   * "private_key_jwt" confidential). Persisted per-server; only meaningful when
   * registrationMode resolves to "cimd". Saves that omit it preserve the stored
   * value (the save-path `?? existing` merge).
   */
  xaaClientAuth?: XaaClientAuthMethod;
  /** Registry credential key for resolving OAuth client ID from env (e.g. "github") */
  oauthCredentialKey?: string;
  /** True for registry servers that use backend-managed preregistered OAuth credentials */
  useRegistryOAuthProxy?: boolean;
  requestTimeout?: number;
  /** Convex _id of the registry server for project/registry bookkeeping */
  registryServerId?: string;
}

export interface OauthTokens {
  client_id: string;
  client_secret: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
}

export interface OAuthTokens {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in?: number;
  scope?: string;
}

export interface AuthSettings {
  serverUrl: string;
  tokens: OAuthTokens | null;
  isAuthenticating: boolean;
  error: string | null;
  statusMessage: StatusMessage | null;
}

export interface StatusMessage {
  type: "success" | "error" | "info";
  message: string;
}

export const DEFAULT_AUTH_SETTINGS: AuthSettings = {
  serverUrl: "",
  tokens: null,
  isAuthenticating: false,
  error: null,
  statusMessage: null,
};

// MCP Resource and Tool types
export interface MCPResource {
  uri: string;
  name: string;
  description?: string;
  mimeType?: string;
}

export interface MCPTool {
  name: string;
  description?: string;
  inputSchema: any;
}

export interface MCPPrompt {
  name: string;
  description?: string;
  arguments?: Array<MCPPromptArgument>;
}

export interface MCPPromptArgument {
  name: string;
  description?: string;
  required?: boolean;
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface ConnectionTestResponse {
  success: boolean;
  error?: string;
  details?: string;
}

export interface ChatStreamEvent {
  type:
    | "text"
    | "tool_call"
    | "tool_result"
    | "elicitation_request"
    | "elicitation_complete"
    | "error";
  content?: string;
  toolCall?: ToolCall;
  toolResult?: {
    id: string | number;
    toolCallId: string | number;
    result?: any;
    error?: string;
    timestamp: Date;
  };
  requestId?: string;
  message?: string;
  schema?: any;
  error?: string;
  timestamp?: Date;
}

// Server status types
export interface ServerStatus {
  status: "ok" | "error";
  timestamp: string;
  service?: string;
}
